import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { ChatContent, ChatLayoutClose } from '~/components/Chat';
import { GroupChatInfo, GroupChatTextBox } from '~/components/ChatRoom';
import { ChatRoomErrorBoundary } from '~/components/ErrorBoundary';
import { Toast } from '~/components/atomic';
import PasswordModal from '~/components/atomic/Modal/PasswordModal';
import { FullScreenLoading, HelmetHead } from '~/components/common';
import ChatShareMessage from '~/components/common/ChatShareMessage/ChatShareMessage';
import {
  useAuthGroupChat,
  useCommonChatState,
  useGetChatList,
  useWebSocket,
} from '~/hooks';
import { MessageResponse } from '~/types';
import { GroupChatStep } from '~/types/groupChat';
import { useUserStore } from '~/store';
import ThreeJSBackground from '../ChatConcept/Background';
import InputHeightContainer from '../Mystery/Chat/InputHeightContainer';
import * as S from './index.styled';

export default function ChatThree() {
  const { id: chatroomId } = useParams();
  const email = useUserStore((state) => state.email);

  const [socketConnectSuccess, setSocketConnectSuccess] = useState(false);
  const [messages, setMessages] = useState<MessageResponse[]>([]);

  const {
    step,
    password,
    setPassword,
    passwordTitle,
    passwordError,
    setPasswordError,
    passwordThumbnails,
    handlePasswordClick,
    successEnter,
    isLoading,
  } = useAuthGroupChat();

  const { webSocket, socketClose, waitingChat, setWaitingChat } = useWebSocket({
    chatRoomId: chatroomId,
    chatType: 'group',
    fetchDone: !isLoading,
    email,
    setMessages,
    socketConnectSuccess,
    setSocketConnectSuccess,
  });

  const {
    safe_chat,
    showCharacter,
    avatarInfo,
    handleChatLayoutClose,
    isChatLayoutShow,
    isLeaveRoomPending,
    handleSetWaitingChatTrue,
  } = useCommonChatState({
    chatType: 'group',
    chatRoomId: chatroomId,
    email,
    setWaitingChat,
  });

  const {
    isChatRoomError,
    title,
    isPaginationPending,
    infoMessageRef,
    chatSectionRef,
    prevHeight,
  } = useGetChatList({
    chatroomId,
    email,
    socketConnectSuccess,
    setMessages,
    chatType: 'group',
    successEnter,
  });

  if (isChatRoomError) {
    return <ChatRoomErrorBoundary />;
  }

  const renderStep = (stepName: GroupChatStep) => {
    switch (stepName) {
      case 'CheckMember':
        return <></>;
      case 'Password':
        return (
          <PasswordModal
            onClick={handlePasswordClick}
            password={password}
            setPassword={setPassword}
            error={passwordError}
            setError={setPasswordError}
            images={passwordThumbnails}
            title={passwordTitle}
          />
        );
      case 'Chat':
        return (
          <>
            {isChatLayoutShow && (
              <S.ChatContainer showCharacter={showCharacter}>
                <ChatContent
                  type={'group'}
                  messages={messages}
                  isLoading={isLoading}
                  waitingChat={waitingChat}
                  isPaginationPending={isPaginationPending}
                  infoMessageRef={infoMessageRef}
                  chatSectionRef={chatSectionRef}
                  prevHeight={prevHeight}
                />
                <InputHeightContainer />
              </S.ChatContainer>
            )}
            <ThreeJSBackground
              avatarInfo={avatarInfo}
              isAvatarVisible={showCharacter}
              isOverviewMode={!isChatLayoutShow}
            />
            {title && chatroomId && (
              <GroupChatTextBox
                webSocket={webSocket}
                chatroomId={chatroomId}
                setWaitingChatTrue={handleSetWaitingChatTrue}
                setMessages={setMessages}
                safe_chat={safe_chat}
              />
            )}
            {title && isChatLayoutShow && (
              <ChatLayoutClose onClick={handleChatLayoutClose} />
            )}
          </>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <HelmetHead title={title || 'Chat'} />

      {isLeaveRoomPending && <FullScreenLoading />}
      {socketClose && <Toast type='error' text='socket close' />}

      <S.Container>{renderStep(step)}</S.Container>

      <GroupChatInfo chatroom_id={chatroomId} />
      <ChatShareMessage />
    </>
  );
}
